
import { useState, useEffect } from 'react';

const SocialSection = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('social-section');
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, []);

  const socialPlatforms = [
    {
      name: 'LinkedIn',
      icon: 'ri-linkedin-fill',
      url: 'https://linkedin.com/company/hashadz',
      description: 'Connect with our professional network and industry insights',
      color: 'from-blue-500 to-blue-600'
    },
    {
      name: 'Instagram',
      icon: 'ri-instagram-fill',
      url: 'https://instagram.com/hashadz',
      description: 'Follow our creative journey and behind-the-scenes content',
      color: 'from-pink-500 to-purple-600'
    },
    {
      name: 'Facebook',
      icon: 'ri-facebook-fill',
      url: 'https://facebook.com/hashadz',
      description: 'Join our community and stay updated with latest news',
      color: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Twitter',
      icon: 'ri-twitter-x-fill',
      url: 'https://twitter.com/hashadz',
      description: 'Get real-time updates and industry thoughts',
      color: 'from-gray-800 to-black'
    },
    {
      name: 'YouTube',
      icon: 'ri-youtube-fill',
      url: 'https://youtube.com/@hashadz',
      description: 'Watch our case studies, and tech insights',
      color: 'from-red-500 to-red-600'
    }
  ];

  return (
    <section id="social-section" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Follow Our Journey
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay updated with our latest projects, industry insights, and team activities. Join our community online and be part of the Hash Adz digital innovation story.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {socialPlatforms.map((platform, index) => (
              <div
                key={platform.name}
                className={`transform transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <a
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-6 bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-12 h-12 bg-gradient-to-r ${platform.color} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                      <i className={`${platform.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900">{platform.name}</h3>
                  </div>
                  <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                    {platform.description}
                  </p>
                  <div className="mt-4 flex items-center text-purple-600 group-hover:text-purple-700 transition-colors duration-300">
                    <span className="text-sm font-medium">Follow us</span>
                    <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300"></i>
                  </div>
                </a>
              </div>
            ))}
          </div>

          {/* Community Stats */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Join Our Growing Community
              </h3>
              <p className="text-lg text-gray-600">
                Connect with fellow innovators, entrepreneurs, and digital enthusiasts
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-team-line text-white text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">5K+</div>
                <div className="text-gray-600">Community Members</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-article-line text-white text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">200+</div>
                <div className="text-gray-600">Insights Shared</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-global-line text-white text-2xl"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-2">50+</div>
                <div className="text-gray-600">Countries Reached</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialSection;
